// AI Resume Search — ask for resumes in plain English, download them (resumes
// only), then optionally have the AI scan the selected resumes against a
// position and tell you who qualifies.
//
// Search: "pull the resumes for Jane Smith and John Doe" or "RNs with 3+ years
// ICU in Houston". Results are candidates who have a resume on file, each with
// a Download button. Analyze: pick some (or all), name a position (defaults to
// Harris County Jail — whose requirements, e.g. RN BSN + 3 yrs combined
// Med-Surg/ER/ICU, come from the live call script), and get a qualify verdict.

import { useState } from 'react';
import { aiResumeApi, candidatesApi, type ResumeSearchHit, type QualifyResult, type QualifyVerdict } from '../../lib/api';
import { useToast } from '../../components/ToastHost';
import { Card, Button, Input, Textarea, Badge, Icon } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

const VERDICT_META: Record<QualifyVerdict, { label: string; tone: BadgeTone }> = {
  qualified:     { label: 'Qualified',     tone: 'good' },
  not_qualified: { label: 'Not qualified', tone: 'danger' },
  maybe:         { label: 'Maybe',         tone: 'warn' },
  unknown:       { label: 'Unknown',       tone: 'neutral' },
};

export default function AiResumeSearch() {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ResumeSearchHit[] | null>(null);
  const [summary, setSummary] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [position, setPosition] = useState('Harris County Jail');
  const [analyzing, setAnalyzing] = useState(false);
  const [verdicts, setVerdicts] = useState<Record<string, QualifyResult>>({});

  async function search() {
    if (!prompt.trim()) return;
    setSearching(true);
    setNote('');
    try {
      const resp = await aiResumeApi.search(prompt.trim());
      setResults(resp.data.results);
      setSummary(resp.data.plan?.summary ?? '');
      setNote(resp.data.note ?? '');
      setSelected(new Set());
      setVerdicts({});
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Search failed';
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  }

  async function download(hit: ResumeSearchHit) {
    try {
      const resp = await candidatesApi.downloadResume(hit.id);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = hit.resume_filename || `${hit.first_name}_${hit.last_name}_resume`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Failed to download resume.');
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    if (!results) return;
    setSelected((prev) => (prev.size === results.length ? new Set() : new Set(results.map((r) => r.id))));
  }

  async function analyze() {
    const ids = [...selected];
    if (ids.length === 0) { toast.error('Select at least one candidate to analyze.'); return; }
    setAnalyzing(true);
    try {
      const resp = await aiResumeApi.analyze(ids, position.trim() || undefined);
      const map: Record<string, QualifyResult> = {};
      for (const r of resp.data.results) map[r.candidate_id] = r;
      setVerdicts((prev) => ({ ...prev, ...map }));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Analysis failed';
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  }

  const loc = (h: ResumeSearchHit) => [h.city, h.state].filter(Boolean).join(', ');

  return (
    <Card
      title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="sparkles" size={16} /> AI Resume Search</span>}
    >
      <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 10 }}>
        Ask for resumes by name or criteria, download them, then scan them against a position.
      </div>
      {/* Search box */}
      <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={'e.g. "resumes for Jane Smith and John Doe" or "RNs with 3+ years ICU or ER in Houston"'}
          style={{ minHeight: 52 }}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void search(); } }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--t3)' }}>Returns only candidates who have a resume on file. Resumes only — no other documents.</span>
          <Button onClick={search} loading={searching} disabled={!prompt.trim()} style={{ marginLeft: 'auto' }}>
            Find resumes
          </Button>
        </div>
      </div>

      {summary && <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 8 }}>Understood: {summary}</div>}
      {note && <div style={{ fontSize: 13, color: 'var(--warn)', marginBottom: 8 }}>{note}</div>}

      {/* Analyze bar (only once there are selectable results) */}
      {results && results.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '10px 12px', background: 'var(--surface-2, #f5f7fb)', border: '1px solid var(--border, #e3e8f0)', borderRadius: 10, marginBottom: 12 }}>
          <button type="button" onClick={toggleAll} className="ui-badge ui-badge--neutral" style={{ cursor: 'pointer' }}>
            {selected.size === results.length ? 'Clear all' : 'Select all'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>{selected.size} selected</span>
          <span style={{ fontSize: 12, color: 'var(--t3)', marginLeft: 'auto' }}>Analyze for position:</span>
          <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Harris County Jail" style={{ width: 200 }} />
          <Button onClick={analyze} loading={analyzing} disabled={selected.size === 0}>
            Analyze selected
          </Button>
        </div>
      )}

      {/* Results */}
      {results === null ? null : results.length === 0 ? (
        <div style={{ padding: 12, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
          No candidates with a resume matched that request.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {results.map((h) => {
            const v = verdicts[h.id];
            const vm = v ? VERDICT_META[v.verdict] : null;
            return (
              <div key={h.id} className="ui-card" style={{ padding: 10, boxShadow: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <input type="checkbox" checked={selected.has(h.id)} onChange={() => toggle(h.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <a href={`/candidates/${h.id}`} style={{ fontWeight: 700, fontSize: 14, color: 'var(--pr)', textDecoration: 'none' }}>
                    {h.first_name} {h.last_name}
                  </a>
                  {h.role && <Badge tone="brand">{h.role}</Badge>}
                  {h.years_experience != null && <span style={{ fontSize: 12, color: 'var(--t3)' }}>{h.years_experience} yrs</span>}
                  {loc(h) && <span style={{ fontSize: 12, color: 'var(--t3)' }}>{loc(h)}</span>}
                  {vm && <Badge tone={vm.tone}>{vm.label}</Badge>}
                  <Button size="sm" variant="secondary" icon={<Icon name="download" size={13} />} onClick={() => void download(h)} style={{ marginLeft: 'auto' }}>
                    Resume
                  </Button>
                </div>
                {h.specialties.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--t3)' }}>{h.specialties.join(' · ')}</div>
                )}
                {v && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border, #eef1f6)', fontSize: 13 }}>
                    {v.summary && <div style={{ color: 'var(--t2)', marginBottom: v.met.length || v.missing.length ? 6 : 0 }}>{v.summary}</div>}
                    {v.met.length > 0 && (
                      <div style={{ color: 'var(--good, #2e7d32)', fontSize: 12 }}>
                        <strong>Meets:</strong> {v.met.join('; ')}
                      </div>
                    )}
                    {v.missing.length > 0 && (
                      <div style={{ color: 'var(--danger, #c62828)', fontSize: 12, marginTop: 2 }}>
                        <strong>Missing:</strong> {v.missing.join('; ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
